import React, { useEffect, useMemo } from "react";
import db from "../../../services/query";
import ariaLogo from "../../../svg/aria-university-logo.svg";
import {
  faDigits,
  buildPrintDocumentTitle,
  usePrintDocumentTitle,
  usePrintData,
} from "./AriaReport";
import "./AbsenceWarningsPrint.css";

const REPORT_TITLE = "هشدار غیبت و محرومیت";
const PRINT_PAGE_STYLE =
  "@page{size:A4 landscape;margin:5mm}" +
  "@page absence-warnings-landscape{size:A4 landscape;margin:5mm}";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cellMark(row, threshold) {
  if (!row) return "";
  if (Number(row.unqualified)) return "محروم";
  if (num(row.absent_percentage) >= num(threshold)) return "هشدار";
  return "";
}

function subjectCell(row, threshold) {
  const mark = cellMark(row, threshold);
  if (!row || !mark) {
    return { mark: "", stats: "" };
  }
  return {
    mark,
    stats: `${num(row.absent_count)}/${num(row.total_count)} · ${num(row.absent_percentage)}%`,
  };
}

/** لایحه کریدیت: محروم در بیش از نصف کریدیت‌های سمستر → محروم سمستر */
function subjectCredits(row) {
  if (!row) return 0;
  const fromField = num(row.effective_credits);
  if (fromField > 0) return fromField;
  const fromHours = num(row.total_count) / 16;
  return fromHours > 0 ? fromHours : 0;
}

function deprivationLabel(subjectRows) {
  let failedCredits = 0;
  let totalCredits = 0;
  for (const row of Object.values(subjectRows || {})) {
    const credits = subjectCredits(row);
    if (credits <= 0) continue;
    totalCredits += credits;
    if (Number(row.unqualified)) failedCredits += credits;
  }
  if (failedCredits <= 0) return "";
  if (totalCredits > 0 && failedCredits > totalCredits / 2) return "محروم سمستر";
  return "محروم مضمون";
}

function studentMatchesStatus(bySubject, status, threshold) {
  const rows = Object.values(bySubject || {});
  if (rows.length === 0) return false;
  if (status === "disqualified") return rows.some((r) => Number(r.unqualified));
  if (status === "warn") {
    return rows.some((r) => {
      const pct = num(r.absent_percentage);
      return pct >= num(threshold) && pct <= 25;
    });
  }
  if (status === "warned") return rows.some((r) => !!r.warned_at);
  if (status === "unwarned") {
    return rows.some((r) => !r.warned_at && cellMark(r, threshold));
  }
  return rows.some((r) => cellMark(r, threshold));
}

const SEMESTER_ORDINAL = {
  اول: 1,
  دوم: 2,
  سوم: 3,
  چهارم: 4,
  پنجم: 5,
  ششم: 6,
  هفتم: 7,
  هشتم: 8,
  نهم: 9,
  دهم: 10,
  یازدهم: 11,
  دوازدهم: 12,
};

function semesterRank(classDesc) {
  const text = String(classDesc || "");
  for (const [word, rank] of Object.entries(SEMESTER_ORDINAL)) {
    if (text.includes(word)) return rank;
  }
  const digit = text.match(/(\d+)/);
  return digit ? Number(digit[1]) : 999;
}

function worstSubjectRow(bySubject) {
  const list = Object.values(bySubject || {});
  if (list.length === 0) return null;
  return list.slice().sort((a, b) => num(b.absent_percentage) - num(a.absent_percentage))[0];
}

function buildReportBrief(matrices, threshold) {
  let students = 0;
  let deprived = 0;
  let semesterDeprived = 0;
  let warned = 0;
  for (const matrix of matrices) {
    for (const student of matrix.students) {
      students += 1;
      const marks = matrix.subjects.map((subject) =>
        cellMark(student.bySubject[subject.subjectId], threshold)
      );
      const label = deprivationLabel(student.bySubject);
      if (label === "محروم سمستر") semesterDeprived += 1;
      else if (label === "محروم مضمون") deprived += 1;
      else if (marks.some((m) => m === "هشدار")) warned += 1;
    }
  }
  return [
    ["تعداد محصلین", students],
    ["تعداد محروم مضمون", deprived],
    ["تعداد محروم سمستر", semesterDeprived],
    ["تعداد هشدار", warned],
  ];
}

/**
 * Pivot flat warning rows (one per student×subject) into class groups with
 * subject columns — same sheet chrome, paper-style matrix body.
 */
function buildClassMatrices(rows, status, threshold) {
  const byClass = new Map();
  for (const row of rows) {
    const classId = String(row.class_id ?? "0");
    if (!byClass.has(classId)) {
      byClass.set(classId, {
        classId,
        classDesc: row.class_desc || "—",
        facultyName: row.faculty_name || "—",
        subjects: new Map(),
        students: new Map(),
      });
    }
    const group = byClass.get(classId);
    const subjectId = String(row.subject_id ?? "");
    if (subjectId && !group.subjects.has(subjectId)) {
      group.subjects.set(subjectId, {
        subjectId,
        subjectName: row.subject_name || "—",
      });
    }
    const studentId = String(row.student_id ?? "");
    if (!studentId) continue;
    if (!group.students.has(studentId)) {
      group.students.set(studentId, {
        studentId,
        studentCode: row.aria_id || row.student_code || "",
        studentName: row.student_name || "—",
        fatherName: row.student_father_name || "—",
        phone: row.student_phone || "—",
        bySubject: {},
      });
    } else if (!group.students.get(studentId).studentCode && (row.aria_id || row.student_code)) {
      group.students.get(studentId).studentCode = row.aria_id || row.student_code;
    }
    if (subjectId) {
      group.students.get(studentId).bySubject[subjectId] = row;
    }
  }

  return Array.from(byClass.values())
    .map((group) => {
      const subjects = Array.from(group.subjects.values()).sort((a, b) =>
        String(a.subjectName).localeCompare(String(b.subjectName), "fa")
      );
      const students = Array.from(group.students.values())
        .filter((student) => studentMatchesStatus(student.bySubject, status, threshold))
        .sort((a, b) =>
          String(a.studentName).localeCompare(String(b.studentName), "fa")
        );
      return {
        classId: group.classId,
        classDesc: group.classDesc,
        facultyName: group.facultyName,
        semesterRank: semesterRank(group.classDesc),
        subjects,
        students,
      };
    })
    .filter((group) => group.students.length > 0)
    .sort(
      (a, b) =>
        String(a.facultyName).localeCompare(String(b.facultyName), "fa") ||
        a.semesterRank - b.semesterRank ||
        String(a.classDesc).localeCompare(String(b.classDesc), "fa")
    );
}

function AbsenceWarningsPrint({ location }) {
  const query = useMemo(
    () => new URLSearchParams(location?.search || window.location.search),
    [location?.search]
  );
  const entryYearId = query.get("entryYearId") || "";
  const warnPct = query.get("warnPct") || "20";
  const statusFilter = query.get("status") || "all";

  useEffect(() => {
    document.body.classList.add("no-linear-gradient");
    // Inject last so this beats global print.css `@page { size: A4 portrait }`.
    const style = document.createElement("style");
    style.setAttribute("data-awp-print-page", "1");
    style.textContent = PRINT_PAGE_STYLE;
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove("no-linear-gradient");
      style.remove();
    };
  }, []);

  const { loading, error, data, summary } = usePrintData(async () => {
    const params = new URLSearchParams(location?.search || window.location.search);
    params.set("export", "1");
    // Matrix needs every subject with attendance (not only محروم) for the credit rule.
    params.set("worstOnly", "0");
    params.set("matrix", "1");
    if (!params.get("entryYearId") && entryYearId) params.set("entryYearId", entryYearId);
    const { data: body } = await db.getAll(`attendance/admin/warnings?${params}`);
    let report = body?.summary?.report || null;
    if ((!report?.semester?.name) && entryYearId) {
      const { data: optionsBody } = await db.getAll(
        `attendance/admin/warnings/options?entryYearId=${encodeURIComponent(entryYearId)}`
      );
      const semester = optionsBody?.summary?.semester || null;
      report = {
        ...(report || {}),
        semester: semester || report?.semester || null,
        faculty_label: report?.faculty_label || "همه",
        class_label: report?.class_label || "همه",
        generated_at:
          report?.generated_at || new Date().toISOString().slice(0, 19).replace("T", " "),
      };
    }
    return { data: body?.data || [], summary: { ...(body?.summary || {}), report } };
  }, [location?.search, entryYearId]);

  const rows = Array.isArray(data) ? data : [];
  const matrices = useMemo(
    () => buildClassMatrices(rows, statusFilter, warnPct),
    [rows, statusFilter, warnPct]
  );
  const reportBrief = useMemo(
    () => buildReportBrief(matrices, warnPct),
    [matrices, warnPct]
  );
  const report = summary?.report || {};
  const semester = report.semester || {};
  const semesterName = faDigits(semester.name || "—");
  const facultyName = report.faculty_label || "همه";
  const className = report.class_label || "همه";
  const showFacultyInfo = facultyName !== "همه";
  const showClassInfo = className !== "همه";
  const showScopeInfo = showFacultyInfo || showClassInfo;
  const scopeLabel = showFacultyInfo ? "پوهنحی:" : "صنف:";
  const scopeValue = showFacultyInfo ? facultyName : className;

  usePrintDocumentTitle(
    buildPrintDocumentTitle([REPORT_TITLE, semester.name, report.class_label || report.faculty_label])
  );

  if (loading) {
    return (
      <div className="awp-root">
        <div className="awp-status">در حال آماده‌سازی گزارش…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="awp-root">
        <div className="awp-status" role="alert">
          {String(error)}
        </div>
      </div>
    );
  }

  return (
    <div className="awp-root awp-matrix">
      <style>{PRINT_PAGE_STYLE}</style>
      <div className="awp-actions">
        <button type="button" onClick={() => window.print()}>
          چاپ / Print
        </button>
      </div>

      <div className="awp-page">
        <header className="awp-header">
          <div className="awp-header-left">
            <div className="awp-org-title">وزارت تحصیلات عالی</div>
            <div className="awp-org-sub">پوهنتون آریا</div>
          </div>

          <div className="awp-header-center">
            <img className="awp-logo" src={ariaLogo} alt="Aria University" />
          </div>

          <div className="awp-header-right">
            <h1 className="awp-title">{REPORT_TITLE}</h1>
            <div className="awp-semester">{semesterName}</div>
          </div>
        </header>

        <div className="awp-separator" aria-hidden="true">
          <span className="line" />
          <span className="diamond" />
          <span className="line" />
        </div>

        <section className={`awp-info${showScopeInfo ? " awp-info-with-scope" : " awp-info-brief-only"}`}>
          {showScopeInfo ? (
          <div className="awp-info-block awp-info-scope">
            <div className="awp-info-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 3 2 8l10 5 8-4v6h2V8L12 3zm-6 9v5c0 2 3 4 6 4s6-2 6-4v-5l-6 3-6-3z" />
              </svg>
            </div>
            <div className="awp-info-content">
              <div className="awp-info-label">{scopeLabel}</div>
              <div className="awp-info-value">{scopeValue}</div>
            </div>
          </div>
          ) : null}

          {showScopeInfo ? (
          <div className="awp-info-divider" aria-hidden="true" />
          ) : null}

          <aside className="awp-result-brief" aria-label="خلص نتایج">
            <h4>خلص نتایج</h4>
            {reportBrief.map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <b>{faDigits(value)}</b>
              </div>
            ))}
          </aside>
        </section>

        {matrices.length === 0 ? (
          <div className="awp-table-wrap">
            <table className="awp-table">
              <tbody>
                <tr>
                  <td className="awp-empty">موردی برای چاپ یافت نشد.</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          matrices.map((matrix) => {
            const subjectCount = matrix.subjects.length;
            const colSpan = 4 + subjectCount + 2;
            return (
              <div className="awp-table-wrap awp-matrix-block" key={matrix.classId}>
                <div className="awp-class-band">
                  {matrix.facultyName} — {matrix.classDesc}
                </div>
                <table className="awp-table awp-matrix-table">
                  <thead>
                    <tr>
                      <th className="awp-col-number">#</th>
                      <th className="awp-col-student">نام محصل</th>
                      <th className="awp-col-father">نام پدر</th>
                      <th className="awp-col-phone">شماره تماس</th>
                      {matrix.subjects.map((subject) => (
                        <th className="awp-col-subject-head" key={subject.subjectId}>
                          {subject.subjectName}
                        </th>
                      ))}
                      <th className="awp-col-warning">تاریخ هشدار</th>
                      <th className="awp-col-note">وضعیت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.students.length === 0 ? (
                      <tr>
                        <td colSpan={colSpan} className="awp-empty">
                          موردی برای چاپ یافت نشد.
                        </td>
                      </tr>
                    ) : (
                      matrix.students.map((student, index) => {
                        const cells = matrix.subjects.map((subject) =>
                          subjectCell(student.bySubject[subject.subjectId], warnPct)
                        );
                        const label = deprivationLabel(student.bySubject);
                        const semesterFail = label === "محروم سمستر";
                        const worst = worstSubjectRow(student.bySubject);
                        const resultText =
                          label ||
                          (worst?.note && String(worst.note).trim()) ||
                          "—";
                        return (
                          <tr
                            key={student.studentId}
                            className={semesterFail ? "awp-row-semester-fail" : undefined}
                          >
                            <td>{faDigits(index + 1)}</td>
                            <td className="student-name">
                              <span className="awp-student-fullname">{student.studentName}</span>
                              {student.studentCode ? (
                                <span className="awp-student-id ltr">
                                  {faDigits(student.studentCode)}
                                </span>
                              ) : null}
                            </td>
                            <td>{student.fatherName}</td>
                            <td className="ltr">{faDigits(student.phone)}</td>
                            {cells.map((cell, i) => (
                              <td
                                key={matrix.subjects[i].subjectId}
                                className={
                                  cell.mark === "محروم"
                                    ? "status-deprived awp-mark-fail awp-subject-cell"
                                    : cell.mark === "هشدار"
                                      ? "awp-mark-warn awp-subject-cell"
                                      : "awp-mark-empty awp-subject-cell"
                                }
                              >
                                {cell.mark ? (
                                  <>
                                    <span className="awp-subject-mark">{cell.mark}</span>
                                    <span className="awp-subject-stats ltr">
                                      {faDigits(cell.stats)}
                                    </span>
                                  </>
                                ) : null}
                              </td>
                            ))}
                            <td>
                              {worst?.warned_at
                                ? faDigits(
                                    String(worst.warned_at).slice(0, 16).replace("T", " ")
                                  )
                                : "—"}
                            </td>
                            <td>{resultText}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            );
          })
        )}

        <div className="awp-bottom">
          <section className="awp-signatures">
            <div className="awp-signature">
              <div className="awp-signature-title">مدیر تدریسی</div>
              <div className="awp-signature-line" />
              <div className="awp-signature-caption">امضا و مهر</div>
            </div>
            <div className="awp-signature">
              <div className="awp-signature-title">رییس پوهنحی</div>
              <div className="awp-signature-line" />
              <div className="awp-signature-caption">امضا و مهر</div>
            </div>
            <div className="awp-signature">
              <div className="awp-signature-title">معاون محصلان</div>
              <div className="awp-signature-line" />
              <div className="awp-signature-caption">امضا و مهر</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AbsenceWarningsPrint;
